/** @jsxImportSource @emotion/react */

import { MissionStatus } from "@a_team/models/dist/MissionObject";
import { css } from "@emotion/react";

import Layout from "components/Layout";
import DefaultSidebar from "components/Layout/Sidebar";
import LoadingFallback from "components/LoadingFallback";
import { StickyHeader } from "components/StickyHeader";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { NewMissionLocation } from "Locations";
import { usePendingMissionSpecs } from "queries/missionSpecs/usePendingMissionSpecs";
import { useRunningMissionSpecs } from "queries/missionSpecs/useRunningMissionSpecs";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useRootStore } from "store";
import CardList from "views/HomeV2/CardList";
import PastProjectsModal from "views/HomeV2/PastProjectsModal";
import StartAProjectCard from "views/HomeV2/StartAProjectCard";
import ActionButton from "views/Mission/TeamSpecV2/components/ActionButton";
import Section from "views/Mission/TeamSpecV2/components/Section";

const getCSSRules: CSSRulesResolver = ({ colors }) => ({
  container: {
    boxSizing: "border-box",
    "> *": {
      boxSizing: "border-box",
    },
    margin: 0,
    maxWidth: 1400,

    paddingRight: "0",
    paddingLeft: "0",

    "@media (max-width: 1023px)": {
      paddingTop: 32,
    },
  },
  section: {
    "@media (max-width: 1023px)": {
      padding: "0 8px",
    },
  },
  workspaceOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: `rgba(255,255,255, .8)`,
    zIndex: 10,
  },
  titleSection: {
    marginTop: -16,
    h1: {
      fontSize: 40,
      lineHeight: "48px",
      margin: 0,
    },
    p: {
      fontSize: 18,
      lineHeight: "26px",
      marginBottom: 0,
      marginTop: 0,
    },
  },
  endedMissionsLink: {
    cursor: "pointer",
    transition: "0.2s ease",

    "&:hover": {
      color: colors.Hannibal[500],
    },
  },
  newProjectBtn: {
    "&[class*=ghost]": {
      width: "100%",
      borderRadius: 16,
      height: 56,
      border: `1px dashed ${colors.Grey[300]}`,

      span: {
        color: colors.Grey[500],
        fontSize: 14,
        lineHeight: "20px",
      },
    },
  },
});

const HomeV2 = () => {
  const styles = useCSSRulesWithTheme(getCSSRules);
  const {
    uiStore: { workspaceOverlay },
  } = useRootStore();
  const [showPastProjects, setShowPastProjets] = useState(false);
  const history = useHistory();

  const {
    data: runningMissionsQueryResult,
    isLoading: loadingRunningMissions,
    fetchNextPage: loadMoreRunningMissions,
    hasNextPage: hasMoreRunningMissions,
    isSuccess: fetchedRunningMissions,
  } = useRunningMissionSpecs();
  const {
    data: pendingMissionsQueryResult,
    isLoading: loadingPendingMissions,
    hasNextPage: hasMorePendingMissions,
    fetchNextPage: loadMorePendingMissions,
    isSuccess: fetchedPendingMissions,
  } = usePendingMissionSpecs();

  const runningMissions = useMemo(() => {
    return (
      runningMissionsQueryResult?.pages.flatMap(({ items }) => items) ?? []
    );
  }, [runningMissionsQueryResult]);

  const pendingMissions = useMemo(() => {
    return (
      pendingMissionsQueryResult?.pages.flatMap(({ items }) => items) ?? []
    ).filter(
      (it) =>
        ![MissionStatus.Ended].includes(it?.platformStatus as MissionStatus)
    );
  }, [pendingMissionsQueryResult]);

  const endedMissions = useMemo(() => {
    return (
      pendingMissionsQueryResult?.pages.flatMap(({ items }) => items) ?? []
    ).filter((it) =>
      [MissionStatus.Ended].includes(it?.platformStatus as MissionStatus)
    );
  }, [pendingMissionsQueryResult]);

  const hasVisibleMissions =
    pendingMissions.length > 0 || runningMissions.length > 0;

  return (
    <Layout
      ctaBlock={<StickyHeader hideBuildTeamCTA />}
      sidebar={<DefaultSidebar />}
    >
      <div css={styles.container} data-testing-id={"home-view"}>
        <div
          css={styles.workspaceOverlay}
          style={{
            display: workspaceOverlay ? "block" : "none",
          }}
        />
        <Section
          css={css(styles.section, styles.titleSection)}
          name={<h1>Projects</h1>}
        >
          <p>View and manage your projects.</p>

          {hasVisibleMissions && (
            <ActionButton
              name="Start a new project"
              variant="ghost"
              onClick={() => history.push(NewMissionLocation)}
              css={styles.newProjectBtn}
              txt={"+ Start a new project"}
            />
          )}

          {!hasVisibleMissions &&
            !loadingRunningMissions &&
            !loadingPendingMissions && <StartAProjectCard />}
        </Section>

        {(!fetchedRunningMissions || runningMissions?.length > 0) && (
          <Section css={styles.section} name="Active projects">
            <CardList cards={runningMissions} />
            {loadingRunningMissions && <LoadingFallback height={200} />}

            {hasMoreRunningMissions && (
              <ActionButton
                txt="Load more"
                loading={loadingRunningMissions}
                onClick={loadMoreRunningMissions}
                size="sm"
              />
            )}
          </Section>
        )}

        {(!fetchedPendingMissions || pendingMissions?.length > 0) && (
          <Section css={styles.section} name="Awaiting launch">
            <CardList cards={pendingMissions} />
            {loadingPendingMissions && <LoadingFallback height={200} />}

            {hasMorePendingMissions && (
              <ActionButton
                txt="Load more"
                loading={loadingPendingMissions}
                onClick={loadMorePendingMissions}
                size="sm"
              />
            )}
          </Section>
        )}

        {endedMissions.length > 0 && (
          <>
            <Section css={styles.section}>
              <span
                css={styles.endedMissionsLink}
                onClick={() => setShowPastProjets(true)}
              >
                See ended projects &rarr;
              </span>
            </Section>
            <PastProjectsModal
              isOpen={showPastProjects}
              close={() => setShowPastProjets(false)}
              endedMissions={endedMissions}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default HomeV2;
